import * as React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import styles from './NotificationList.sass'
import NotificationListViewModel, {
  NotificationGroup,
} from './NotificationListViewModel'
import { useActivation } from 'hooks/mobx-hooks'
import ErrorBlock from 'components/ErrorBlock'
import NotificationItemViewModel, {
  NotificationGroupType,
} from './NotificationItemViewModel'
import Button from 'components/Button'
import Avatar from 'components/Avatar'
import { timeago } from 'utils/date-util'
import Link from 'components/Link'
import InfiniteScrollLoader from 'components/InfiniteScrollLoader'
import { Task } from 'mobx-task'
import { EmptyState, EmptyStateIllustrationImage } from 'components/EmptyState'
import MinidownContent from 'components/MinidownContent'
import { useBrandingSettings } from 'hooks/branding'

interface Props {
  vm: NotificationListViewModel
}

export default observer(function NotificationList({ vm }: Props) {
  useActivation(vm)
  const { corporateMode } = useBrandingSettings()

  return (
    <div className={cx(styles.root)}>
      {vm.scroller.fetch.match({
        pending: () => null,
        rejected: (err) => <ErrorView err={err} />,
        resolved: () => (
          <>
            {vm.notificationGroups.length === 0 && (
              <EmptyState
                hideIllustration={corporateMode}
                pad
                small
                image={
                  <EmptyStateIllustrationImage
                    src={require('assets/images/img_no-notifications.svg')}
                  />
                }
                title="No notifications right now"
                subtitle={
                  <>
                    As soon as there are any status updates, <br />
                    we'll show the notifications here
                  </>
                }
              />
            )}
            {vm.notificationGroups.map((group, i) => (
              <NotificationGroupView
                key={i}
                group={group}
                markAllAsRead={i === 0 ? vm.markAllAsRead : undefined}
              ></NotificationGroupView>
            ))}
          </>
        ),
      })}
      <InfiniteScrollLoader scrollProvider={vm.scroller} />
    </div>
  )
})

const ErrorView = ({ err }: { err: unknown }) => (
  <div className={styles.error}>
    <ErrorBlock error={err} />
  </div>
)

interface NotificationGroupViewProps {
  group: NotificationGroup
  markAllAsRead?: Task<any, any>
}

const NotificationGroupView = observer(function NotificationGroup({
  group,
  markAllAsRead,
}: NotificationGroupViewProps) {
  return (
    <div className={styles.group}>
      <div className={styles.groupHeader}>
        <p className={styles.groupTitle}>
          {group.type === NotificationGroupType.Older ? 'Older' : 'Today'}
        </p>
        {markAllAsRead && (
          <div className={styles.groupActions}>
            <Button
              type="text"
              disabled={markAllAsRead.pending}
              loading={markAllAsRead.pending}
              onClick={markAllAsRead}
            >
              Mark All as Read
            </Button>
          </div>
        )}
      </div>
      {group.notifications.map((item) => (
        <NotificationItem key={item.id} vm={item} />
      ))}
    </div>
  )
})

const NotificationItem = observer(function NotificationItem({
  vm,
}: {
  vm: NotificationItemViewModel
}) {
  return (
    <Link
      className={styles.item}
      to={vm.actionUrl || undefined}
      onClick={vm.markAsRead}
    >
      <div className={styles.imageContainer}>
        <Avatar
          size={32}
          subject={{
            avatar:
              vm.imageSource || require('assets/logos/logo_worklayer-blue.svg'),
          }}
        />
      </div>
      <div className={styles.content}>
        <p className={styles.title} title={vm.plainText}>
          <MinidownContent value={vm.text} />
        </p>
        <p className={styles.subtitle}>
          {timeago(vm.dispatchedAt)} &bull; {vm.workspaceName}
        </p>
      </div>
      <div className={styles.meta}>
        {!vm.isRead && <div className={styles.unread} />}
      </div>
    </Link>
  )
})
